import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useMutateUploadDocument } from '../../api/documentHub';
import { DocumentMimeTypes, DocumentTypes } from '../../models/documentHub';
import CloseIcon from '@mui/icons-material/Close';
import BackupRoundedIcon from '@mui/icons-material/BackupRounded';
import './DocumentHubPage.scss';

export const DocumentUploadButton = () => {
  const uploadMutation = useMutateUploadDocument();
  const fileInput = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    'Something went wrong. Please try again.'
  );
  const [selectedFolder, setSelectedFolder] = useState(DocumentTypes.OTHER);
  const [selectedFileType, setSelectedFileType] = useState('');

  const [incorrectFileType, setIncorrectFileType] = useState(false);

  const [incorrectFileSize, setIncorrectFilSize] = useState(false);

  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [uploadRequest, setUploadRequest] = useState({
    documentType: DocumentTypes.OTHER,
    documentName: '',
    base64Document: '',
  });

  const handleUploadClick = () => {
    setIncorrectFileType(false);
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const handleModalClose = () => {
    setUploadRequest({
      documentType: DocumentTypes.OTHER,
      documentName: '',
      base64Document: '',
    });
    setSelectedFileType('');
    setIsOpen(false);
  };

  const validataeFileType = (
    selectedFolder: DocumentTypes,
    fileType: string
  ) => {
    if (
      selectedFolder === DocumentTypes.MEDIA &&
      ![
        DocumentMimeTypes.MP4 as string,
        DocumentMimeTypes.JPEG as string,
        DocumentMimeTypes.PNG as string,
      ].includes(fileType)
    ) {
      setIncorrectFileType(true);
      return;
    } else if (
      selectedFolder === DocumentTypes.OTHER &&
      ![
        DocumentMimeTypes.DOCX as string,
        DocumentMimeTypes.PDF as string,
        DocumentMimeTypes.XLSX as string,
      ].includes(fileType)
    ) {
      setIncorrectFileType(true);
      return;
    } else {
      setIncorrectFileType(false);
    }
  };

  const validateFileSize = (file: File) => {
    const fileSize: number = file.size / 1048576;
    if (fileSize > 4.5) {
      setIncorrectFilSize(true);
    } else {
      setIncorrectFilSize(false);
    }
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      validataeFileType(selectedFolder, file.type);
      validateFileSize(file);
      if (!incorrectFileType) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setUploadRequest({
            documentType: selectedFolder,
            documentName: file.name,
            base64Document: base64String.split(',')[1],
          });
          setSelectedFileType(file.type);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleChange = (event: SelectChangeEvent<DocumentTypes>) => {
    setSelectedFolder(event.target.value as DocumentTypes);
    validateFileSize(uploadFile as File);
    if (selectedFileType) {
      validataeFileType(event.target.value as DocumentTypes, selectedFileType);
      setUploadRequest({
        ...uploadRequest,
        documentType: event.target.value as DocumentTypes,
      });
    }
  };
  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
      <Box>
        <input
          type="file"
          ref={fileInput}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          disabled={uploadMutation.isPending}
          className="upload"
          onClick={() => setIsOpen(true)}
        >
          {uploadMutation.isPending ? 'Uploading Document' : 'Upload Document'}
        </Button>
      </Box>
      <Dialog
        data-testid="file-upload-component"
        className="file-upload-modal"
        open={isOpen}
        onClose={() => handleModalClose()}
        aria-labelledby="auth-dialog-title"
      >
        <Box className="container" sx={{ backgroundColor: '#F2F4F6' }}>
          <Button
            variant="transparent"
            className="close-btn"
            onClick={() => handleModalClose()}
            sx={{ color: 'white' }}
          >
            <CloseIcon sx={{ fill: '#49454F' }} />
          </Button>
          <DialogContent
            className="content"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{ marginBottom: '16px' }}
              variant="h2"
              className="search-filters-title"
            >
              Upload Document
            </Typography>
            <Box
              onClick={handleUploadClick}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                background: 'white',
                padding: '12px',
                marginBottom: '24px',
                borderRadius: '4px',
              }}
            >
              <BackupRoundedIcon
                sx={{ width: '54px', height: '54px', color: '#0D3B66' }}
              />

              <Typography
                sx={{ fontSize: '14px', margin: '12px' }}
                className="search-filters-title"
              >
                Click here to browse for documents
              </Typography>

              {selectedFolder === DocumentTypes.MEDIA && (
                <Typography
                  sx={{ fontSize: '12px' }}
                  className="search-filters-title"
                >
                  Supports .png .jpeg .mp4
                </Typography>
              )}

              {selectedFolder === DocumentTypes.OTHER && (
                <Typography
                  sx={{ fontSize: '12px' }}
                  className="search-filters-title"
                >
                  Supports .pdf .docx .xlxs
                </Typography>
              )}
              {uploadRequest.documentName && (
                <Typography
                  sx={{ fontSize: '12px', margin: '8px' }}
                  variant="h5"
                  className="search-filters-title"
                >
                  {uploadRequest.documentName}
                </Typography>
              )}
              {incorrectFileType && (
                <Typography
                  sx={{ fontSize: '12px', color: 'red' }}
                  className="search-filters-title"
                >
                  Incorrect file type
                </Typography>
              )}
              {incorrectFileSize && (
                <Typography
                  sx={{ fontSize: '12px', color: 'red' }}
                  className="search-filters-title"
                >
                  File size must be less than 4.5MB
                </Typography>
              )}
            </Box>

            <FormControl
              sx={{ m: 0, width: '100%', background: 'white' }}
              size="small"
            >
              <InputLabel id="demo-select-small-label" sx={{ top: '-8px' }}>
                Select Folder
              </InputLabel>
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                value={selectedFolder}
                onChange={(e) => handleChange(e)}
              >
                <MenuItem value={DocumentTypes.OTHER}>Other</MenuItem>
                <MenuItem value={DocumentTypes.MEDIA}>Media</MenuItem>
              </Select>
            </FormControl>
            <Button
              sx={{
                width: '100%',
                marginTop: '24px',
              }}
              disabled={
                uploadMutation.isPending ||
                !uploadRequest.base64Document ||
                incorrectFileType ||
                incorrectFileSize
              }
              onClick={() => {
                uploadMutation.mutate(uploadRequest, {
                  onSuccess: () => handleModalClose(),
                  onError: (error) => {
                    if (error.message.includes('413')) {
                      setErrorMessage('File size must be less than 4.5MB');
                    } else {
                      setErrorMessage(
                        'Something went wrong. Please try again.'
                      );
                    }
                    setShowError(true);
                  },
                });
              }}
            >
              Save
            </Button>
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};
